import React from 'react';
import { MapPin, Clock, Phone, Mail, Navigation, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { salonInfo } from '../data/mock';

const LocationPage = () => {
  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="relative py-24 px-4 bg-gradient-to-br from-[#EBF4F6] via-white to-[#EBF4F6]/50 text-gray-900 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <Badge className="mb-4 bg-[#3D7A8C]/10 text-[#3D7A8C] border-0">
            <MapPin className="w-4 h-4 mr-1 inline" />
            Visit Us
          </Badge>
          <h1 className="font-serif text-5xl md:text-6xl font-bold mb-6">
            Location & Hours
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Find us at our location. We look forward to welcoming you!
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Address Card */}
          <Card className="border-2 border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-[#3D7A8C]/10 rounded-xl">
                  <MapPin className="w-8 h-8 text-[#3D7A8C]" />
                </div>
                <div>
                  <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">Our Address</h2>
                  <p className="text-gray-600">Easy to find, convenient parking available</p>
                </div>
              </div>
              <div className="space-y-2 mb-6">
                <p className="text-lg text-gray-800">{salonInfo.address.street}</p>
                <p className="text-lg text-gray-800">
                  {salonInfo.address.city}, {salonInfo.address.state} {salonInfo.address.zip}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${salonInfo.address.street}, ${salonInfo.address.city}, ${salonInfo.address.state}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button className="w-full bg-[#3D7A8C] hover:bg-[#356A7A] text-white">
                    <Navigation className="mr-2 h-4 w-4" />
                    Get Directions
                  </Button>
                </a>
              </div>
              <div className="mt-6 pt-6 border-t-2 border-gray-100">
                <div className="flex items-center gap-3 text-gray-700">
                  <Car className="w-5 h-5 text-[#3D7A8C]" />
                  <span className="text-sm">Free parking available in adjacent lot</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Hours Card */}
          <Card className="border-2 border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-[#3D7A8C]/10 rounded-xl">
                  <Clock className="w-8 h-8 text-[#3D7A8C]" />
                </div>
                <div>
                  <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">Business Hours</h2>
                  <p className="text-gray-600">Open 6 days a week to serve you</p>
                </div>
              </div>
              <div className="space-y-3">
                {daysOfWeek.map((day) => {
                  const isToday = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() === day;
                  const hours = salonInfo.hours[day];
                  const isClosed = hours === 'Closed';
                  
                  return (
                    <div 
                      key={day}
                      className={`flex justify-between items-center p-3 rounded-lg transition-all duration-300 ${
                        isToday ? 'bg-[#3D7A8C]/10 border-2 border-[#3D7A8C]/30' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className={`font-semibold capitalize ${isToday ? 'text-[#3D7A8C]' : 'text-gray-700'}`}>
                        {day}
                        {isToday && <Badge className="ml-2 bg-[#3D7A8C] text-white text-xs">Today</Badge>}
                      </span>
                      <span className={`${isClosed ? 'text-gray-400' : isToday ? 'text-[#3D7A8C] font-semibold' : 'text-gray-600'}`}>
                        {hours}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map */}
        <Card className="border-2 border-gray-100 shadow-xl overflow-hidden mb-12">
          <div className="relative h-96 md:h-[500px] bg-gray-200">
            <iframe
              title="Salon Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.7!2d77.67!3d12.93!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae132b0d9f5555%3A0x7777777777777777!2sKempapura%20Main%20Rd%2C%20Kempapura%2C%20Bellandur%2C%20Bengaluru%2C%20Karnataka%20560037!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0"
            ></iframe>
          </div>
        </Card>

        {/* Contact Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-2 border-gray-100 hover:border-[#3D7A8C]/50 transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-[#3D7A8C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-[#3D7A8C]" />
              </div>
              <h3 className="font-serif text-xl font-bold text-gray-900 mb-2">Call Us</h3>
              <a href={`tel:${salonInfo.phone}`} className="text-[#3D7A8C] hover:text-[#356A7A] font-semibold text-lg">
                {salonInfo.phone}
              </a>
              <p className="text-gray-600 text-sm mt-2">Available during business hours</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-100 hover:border-[#3D7A8C]/50 transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-[#3D7A8C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#3D7A8C]" />
              </div>
              <h3 className="font-serif text-xl font-bold text-gray-900 mb-2">Email Us</h3>
              <a href={`mailto:${salonInfo.email}`} className="text-[#3D7A8C] hover:text-[#356A7A] font-semibold break-all">
                {salonInfo.email}
              </a>
              <p className="text-gray-600 text-sm mt-2">We respond within 24 hours</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-100 hover:border-[#3D7A8C]/50 transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-[#3D7A8C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Navigation className="w-8 h-8 text-[#3D7A8C]" />
              </div>
              <h3 className="font-serif text-xl font-bold text-gray-900 mb-2">Get Directions</h3>
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${salonInfo.address.street}, ${salonInfo.address.city}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#3D7A8C] hover:text-[#356A7A] font-semibold"
              >
                Open in Maps
              </a>
              <p className="text-gray-600 text-sm mt-2">Easy access & parking</p>
            </CardContent>
          </Card>
        </div>

        {/* Additional Info */}
        <div className="mt-12 bg-gradient-to-br from-[#EBF4F6] to-[#D7E9ED] rounded-2xl p-8 border-2 border-[#3D7A8C]/20">
          <h3 className="font-serif text-2xl font-bold text-gray-900 mb-4 text-center">
            What to Know Before Your Visit
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-[#3D7A8C] rounded-full mt-2"></div>
              <p className="text-gray-700">Please arrive 10 minutes early for your first appointment</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-[#3D7A8C] rounded-full mt-2"></div>
              <p className="text-gray-700">Free Wi-Fi available for all guests</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-[#3D7A8C] rounded-full mt-2"></div>
              <p className="text-gray-700">Complimentary beverages and refreshments</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-[#3D7A8C] rounded-full mt-2"></div>
              <p className="text-gray-700">Wheelchair accessible entrance and facilities</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPage;
